import { useId } from 'react';
import PropTypes from 'prop-types';
import './CurlyArrow.css';

/**
 * CurlyArrow — пунктирная стрелка-связка (#1330).
 *
 * ## Один атом на все стрелки витрины
 *
 * Их было три и все разные: общая дуга между окнами-мокапами, петля от
 * обращения к форме на главной и волна от кода к лобовому стеклу. Две
 * последние жили инлайновыми `<svg>` прямо в разметке страниц — у каждой свой
 * `stroke-width`, свой пунктир и свой цвет, и «поправить стрелки» означало
 * искать их по трём файлам. Теперь рисунок выбирается пропом `shape`, а
 * толщина, цвет и пунктир приходят из токенов.
 *
 * ## Почему у каждой формы свой `viewBox`
 *
 * Пропорция коробки в CSS должна совпадать с пропорцией `viewBox`, иначе
 * `preserveAspectRatio` впишет рисунок с полями и остриё уедет с того места,
 * куда его ставили. Поэтому форма несёт свой `viewBox`, а потребитель задаёт
 * ширину и `aspect-ratio` из той же пары чисел.
 *
 * Исключение — `stretch`: там коробку растягивают намеренно, а обводку от
 * растяжения спасает `vector-effect` в CSS.
 *
 * Лежит в `pages/shared/`, поэтому CSS не инлайнится в Shadow DOM дилера.
 *
 * @param {object} props
 * @param {'loop' | 'wave' | 'arc' | 'sweep' | 'dive'} [props.shape] — рисунок.
 * @param {boolean} [props.stretch] — тянуть по коробке, а не вписывать.
 * @param {boolean} [props.startDot] — точка в начале линии. С `stretch` не
 *   сочетается: коробку там тянут намеренно, и круг растягивается в эллипс
 *   (обводку спасает `vector-effect`, заливку — нечем).
 */
const SHAPES = {
  /**
   * Петля: вниз, виток, вправо. Связывает обращение слева с формой справа —
   * промежуток между ними широкий и низкий, и прямая дуга на нём читалась
   * разделителем колонок.
   */
  loop: {
    viewBox: '0 0 416 208',
    dot: { cx: 6, cy: 16, r: 5 },
    line: 'M6 24 C 6 74, 30 112, 70 130 C 110 148, 150 150, 150 120 C 150 96, 120 96, 116 126 C 112 156, 152 174, 202 168 C 280 160, 332 140, 384 112',
    head: 'M369 130 L384 112 L360 114',
  },
  /**
   * Волна справа налево: уходит с плитки кода, огибает её угол и ложится
   * остриём на стекло. Ровная дуга на этом расстоянии читалась указателем
   * «куда-то влево».
   */
  wave: {
    viewBox: '0 0 236 100',
    line: 'M232 14 C 208 4, 196 30, 172 30 C 150 30, 146 8, 124 12 C 100 16, 100 46, 76 52 C 56 57, 46 58, 26 70',
    head: 'M36 56 L26 70 L43 67',
  },
  /**
   * Пологая волна на ШИРОКИЙ и низкий промежуток: от двери салона на общем
   * плане к коду на крупном плане входа.
   *
   * Отдельная форма, а не растянутая петля. Пропорция коробки обязана
   * совпадать с пропорцией `viewBox`, иначе рисунок вписывается с полями и
   * остриё уезжает с цели; растягивать же петлю (2:1) на промежуток 5:1
   * значит расплющить и её виток, и круглую точку в начале — точка стала бы
   * овалом. Поэтому у промежутка своя форма с пропорцией 5:1.
   */
  sweep: {
    viewBox: '0 0 500 100',
    dot: { cx: 8, cy: 82, r: 5 },
    line: 'M8 82 C 70 82, 96 40, 160 34 C 224 28, 260 62, 322 56 C 384 50, 430 30, 482 22',
    head: 'M466 12 L484 22 L464 34',
  },
  /**
   * Наклонный спуск с витком: от заголовка слева к трубке на экране телефона
   * в центре блока.
   *
   * У петли начало и остриё лежат близко по высоте (11% и 54% коробки), и на
   * таком промежутке она шла почти горизонтально — уходила из-под заголовка
   * вбок и мимо кнопки. Здесь линия начинается у верхней кромки коробки и
   * кончается у нижней (5% и 95%), то есть спускается вдвое круче при той же
   * пропорции 2:1.
   */
  dive: {
    viewBox: '0 0 416 208',
    dot: { cx: 8, cy: 10, r: 5 },
    line: 'M8 18 C 8 60, 24 92, 64 110 C 104 128, 140 124, 140 104 C 140 86, 114 88, 118 112 C 124 148, 172 170, 236 182 C 300 194, 352 196, 392 198',
    head: 'M372 186 L394 199 L369 206',
  },
  /**
   * Дуга между двумя окнами-мокапами: слева переписка покупателя, справа
   * инбокс команды, и это ОДНО событие с двух сторон. Кривая, а не прямая:
   * прямая между двумя окнами читается разделителем колонок, а дуга — как
   * перенос.
   */
  arc: {
    viewBox: '0 0 96 64',
    dot: { cx: 4, cy: 52, r: 4 },
    line: 'M4 52 C 28 52, 44 12, 82 12',
    head: 'M74 6 L84 12 L74 18',
  },
};

export const CurlyArrow = ({
  shape = 'arc',
  stretch = false,
  startDot = false,
  className = '',
}) => {
  const { viewBox, dot, line, head } = SHAPES[shape];
  // Маска у каждого экземпляра своя: на странице контактов стрелок две, и с
  // общим `id` вторая забирала бы маску первой. Двоеточия из `useId`
  // (`:r1:`) убираем — в ссылке `url(#…)` они легальны, но любой селектор по
  // такому `id` потом требует экранирования, и это ловушка на ровном месте.
  const maskId = `curly-arrow-${useId().replace(/:/g, '')}`;

  return (
    <svg
      className={`curly-arrow curly-arrow--${shape}${
        stretch ? ' curly-arrow--stretch' : ''
      }${className ? ` ${className}` : ''}`}
      viewBox={viewBox}
      fill="none"
      preserveAspectRatio={stretch ? 'none' : undefined}
      aria-hidden="true"
      focusable="false"
    >
      <defs>
        {/*
          Маска-«перо»: та же кривая, но сплошной толстой обводкой. Её
          `stroke-dashoffset` и анимируется — линия проявляется ВДОЛЬ СВОЕГО
          пути, а не открывается вертикальной шторкой.

          `pathLength="1"` избавляет от измерения длины в JS: браузер сам
          пересчитывает пунктир в доли пути, и один и тот же `dasharray: 1 1`
          работает для всех трёх форм, какой бы длины они ни были.
        */}
        <mask id={maskId} maskUnits="userSpaceOnUse">
          <path className="curly-arrow__reveal" d={line} pathLength="1" />
        </mask>
      </defs>
      {/* Точка в начале: линия должна откуда-то выходить, а обрубленный конец
          пунктира читается обрывом. */}
      {startDot && dot && (
        <circle
          className="curly-arrow__dot"
          cx={dot.cx}
          cy={dot.cy}
          r={dot.r}
        />
      )}
      <path className="curly-arrow__line" d={line} mask={`url(#${maskId})`} />
      {/* Остриё вне маски: оно не лежит на линии, и «проявить» его вдоль пути
          нечем. Оно появляется отдельно, когда перо до него доходит. */}
      <path className="curly-arrow__head" d={head} />
    </svg>
  );
};

CurlyArrow.propTypes = {
  shape: PropTypes.oneOf(['loop', 'wave', 'arc', 'sweep', 'dive']),
  stretch: PropTypes.bool,
  startDot: PropTypes.bool,
  className: PropTypes.string,
};

export default CurlyArrow;
